// v13
// Path: frontend/src/components/SyncHub/ConnectionBindingDrawer.jsx
// Inputs: isOpen, onClose, editingConnection, targetContainer, onSaveSuccess, authHeaders, apps, driverTypes, authModels, integrationAccounts, onNotification
// Outputs: Compact, high-density side drawer component for hierarchical credential assignment and explicit target binding
// Summary: Hierarchical credential injection at either Service Instance or Target Container level, verified Network Interface binding mutations, explicit-only target pre-population without speculative interface or instance auto-selection, 'Choose (option) ...' placeholder standard across all selectors, top binding configuration without default regression, nested visual indentation, and standardized icon triggers.

import React, { useState, useEffect, useMemo } from 'react';
import SearchableSelect from './SearchableSelect';

export default function ConnectionBindingDrawer({
    isOpen,
    onClose,
    editingConnection,
    targetContainer,
    onSaveSuccess,
    authHeaders,
    apps = [],
    driverTypes = [],
    authModels = [],
    integrationAccounts = [],
    onNotification
}) {
    // Top-level topology state
    const [topology, setTopology] = useState({
        organizations: [],
        hosts: [],
        instances: [],
        containers: [],
        authModels: [],
        credentials: [],
        driverTypes: [],
        interfaces: [],
        interfaceTypes: []
    });

    // Selected Target IDs (Cascading: Org -> Host -> Interface/Instance -> Container)
    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [selectedHostId, setSelectedHostId] = useState('');
    const [selectedInterfaceId, setSelectedInterfaceId] = useState('');
    const [selectedInstanceId, setSelectedInstanceId] = useState('');
    const [selectedContainerId, setSelectedContainerId] = useState('');

    // Hierarchical Credential Assignment: 'INSTANCE' | 'CONTAINER' | null
    const [credentialScopeLevel, setCredentialScopeLevel] = useState(null);
    const [discoveredDatabases, setDiscoveredDatabases] = useState([]);
    const [discoveringDatabases, setDiscoveringDatabases] = useState(false);
    const [instanceDiscoverError, setInstanceDiscoverError] = useState(null);

    // Selected Credential & Binding Details
    const [selectedCredentialId, setSelectedCredentialId] = useState('');
    const [bindingName, setBindingName] = useState('');
    const [bindingNotes, setBindingNotes] = useState('');
    const [isActive, setIsActive] = useState(true);

    // Inline "Add / Edit" Toggles
    const [isAddingHost, setIsAddingHost] = useState(false);
    const [isEditingHost, setIsEditingHost] = useState(false);
    const [isAddingInterface, setIsAddingInterface] = useState(false);
    const [isEditingInterface, setIsEditingInterface] = useState(false);
    const [isAddingInstance, setIsAddingInstance] = useState(false);
    const [isEditingInstance, setIsEditingInstance] = useState(false);
    const [isAddingContainer, setIsAddingContainer] = useState(false);
    const [isEditingContainer, setIsEditingContainer] = useState(false);
    const [isAddingOrg, setIsAddingOrg] = useState(false);
    const [isAddingAuthModel, setIsAddingAuthModel] = useState(false);
    const [isEditingCredential, setIsEditingCredential] = useState(false);

    // Inline Form States
    const [newHostForm, setNewHostForm] = useState({ HostName: '', DisplayName: '', HostType: 'ON_PREM_SERVER', OrganizationID: '' });
    const [newInterfaceForm, setNewInterfaceForm] = useState({ EndpointAddress: '', InterfaceTypeID: 3, NetworkIdentifier: '', PriorityOrder: 10, Notes: '' });
    const [newInstanceForm, setNewInstanceForm] = useState({ ServiceName: '', EngineType: 'SQL Server', ListenPort: 1433, OrganizationID: '' });
    const [newContainerForm, setNewContainerForm] = useState({ ContainerName: '', ContainerType: 'RELATIONAL_DATABASE', ContainerRole: 'PRODUCTION', OrganizationID: '' });
    const [newOrgForm, setNewOrgForm] = useState({ OrganizationName: '', OrganizationType: 'Store', ParentOrganizationID: '' });
    const [newAuthModelForm, setNewAuthModelForm] = useState({ AuthModelCode: '', AuthModelName: '', Description: '' });

    // New Credential Form State
    const [newCredForm, setNewCredForm] = useState({
        CredentialName: '',
        SourceTypeID: 1,
        AuthModelID: 3,
        Username: 'sa',
        password: '',
        realmId: '',
        clientId: '',
        clientSecret: '',
        accessToken: '',
        refreshToken: '',
        environment: 'sandbox',
        baseUrl: '',
        apiKey: '',
        headerName: 'Authorization',
        domain: '',
        notes: ''
    });

    // Test Connection & Submission State
    const [testingConnection, setTestingConnection] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loadingTopology, setLoadingTopology] = useState(false);

    // Fetch Topology on open
    const loadTopology = async () => {
        setLoadingTopology(true);
        try {
            const res = await fetch('/api/catalog/topology', { headers: authHeaders });
            const data = await res.json();
            if (data.success && data.data) {
                setTopology(data.data);
            }
        } catch (err) {
            console.error('Failed to load topology:', err);
        } finally {
            setLoadingTopology(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadTopology();
        }
    }, [isOpen]);

    // Live Instance Discovery Function
    const discoverInstanceTargets = async (instId, ifaceId, credId) => {
        if (!instId || !credId) return;
        setDiscoveringDatabases(true);
        setInstanceDiscoverError(null);
        try {
            const res = await fetch(`/api/catalog/instances/${instId}/discover`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    interfaceId: ifaceId || selectedInterfaceId,
                    credentialId: credId
                })
            });
            const data = await res.json();
            if (data.success && data.data?.databases) {
                setDiscoveredDatabases(data.data.databases);
                if (onNotification) {
                    onNotification(`Live Discovery: Found ${data.data.count} databases on instance!`, 'success');
                }
            } else {
                setInstanceDiscoverError(data.error || 'Failed to enumerate live databases');
            }
        } catch (err) {
            setInstanceDiscoverError(err.message);
        } finally {
            setDiscoveringDatabases(false);
        }
    };

    // Initialize selection states when editingConnection or targetContainer changes
    useEffect(() => {
        if (!isOpen) return;

        setTestResult(null);
        setIsAddingHost(false);
        setIsEditingHost(false);
        setIsAddingInterface(false);
        setIsEditingInterface(false);
        setIsAddingInstance(false);
        setIsEditingInstance(false);
        setIsAddingContainer(false);
        setIsEditingContainer(false);
        setIsAddingOrg(false);
        setIsAddingAuthModel(false);
        setIsEditingCredential(false);
        setDiscoveredDatabases([]);
        setInstanceDiscoverError(null);

        if (editingConnection) {
            const orgId = editingConnection.EffectiveOrganizationID || editingConnection.OrganizationID || editingConnection.HostOrgID || editingConnection.InstanceOrgID || editingConnection.ContainerOrgID || '';
            setSelectedOrgId(orgId ? String(orgId) : '');
            setSelectedHostId(editingConnection.HostID ? String(editingConnection.HostID) : '');
            
            // Strict Zero-Fallback: If no interface is explicitly bound on the connection, do NOT fall back to host interfaces
            const resolvedIfaceId = editingConnection.InterfaceID
                ? String(editingConnection.InterfaceID)
                : '';
            setSelectedInterfaceId(resolvedIfaceId);
            setSelectedInstanceId(editingConnection.InstanceID ? String(editingConnection.InstanceID) : '');

            // Accurately detect whether this connection is bound at the INSTANCE level vs CONTAINER level
            const isInstanceLevel = !!editingConnection.isInheritedFromInstance ||
                (!editingConnection.BindingContainerID && !!editingConnection.BindingInstanceID) ||
                editingConnection.ContainerID === '__INSTANCE__';

            if (isInstanceLevel) {
                setSelectedContainerId('__INSTANCE__');
                setCredentialScopeLevel('INSTANCE');
            } else {
                setSelectedContainerId(editingConnection.ContainerID ? String(editingConnection.ContainerID) : '');
                setCredentialScopeLevel(editingConnection.CredentialID ? 'CONTAINER' : null);
            }
            setSelectedCredentialId(editingConnection.CredentialID ? String(editingConnection.CredentialID) : '');
            setBindingName(editingConnection.BindingName || editingConnection.CredentialName || editingConnection.ContainerName || '');
            setBindingNotes(editingConnection.BindingNotes || '');
            setIsActive(editingConnection.BindingIsActive !== undefined ? !!editingConnection.BindingIsActive : true);

            const isSaaS = editingConnection.SourceTypeID === 2 || editingConnection.AuthModelID === 1 || editingConnection.AuthModelID === 2;
            setNewCredForm({
                CredentialName: editingConnection.CredentialName || '',
                SourceTypeID: editingConnection.SourceTypeID || (isSaaS ? 2 : 1),
                AuthModelID: editingConnection.AuthModelID || (isSaaS ? 2 : 3),
                Username: editingConnection.Username || (isSaaS ? '' : 'sa'),
                password: '',
                realmId: isSaaS ? (editingConnection.realmId || '') : '',
                clientId: isSaaS ? (editingConnection.clientId || '') : '',
                clientSecret: '',
                accessToken: '',
                refreshToken: '',
                environment: editingConnection.environment || 'sandbox',
                baseUrl: isSaaS ? (editingConnection.baseUrl || 'https://sandbox-quickbooks.api.intuit.com') : '',
                apiKey: '',
                headerName: 'Authorization',
                domain: editingConnection.rawConfig?.domain || '',
                notes: editingConnection.CredentialNotes || ''
            });
        } else if (targetContainer) {
            const orgId = targetContainer.EffectiveOrganizationID || targetContainer.OrganizationID || targetContainer.HostOrgID || targetContainer.InstanceOrgID || targetContainer.ContainerOrgID || '';
            setSelectedOrgId(orgId ? String(orgId) : '');
            setSelectedHostId(targetContainer.HostID ? String(targetContainer.HostID) : '');
            
            const targetIfaceId = targetContainer.InterfaceID ? String(targetContainer.InterfaceID) : '';
            setSelectedInterfaceId(targetIfaceId);
            setSelectedInstanceId(targetContainer.InstanceID ? String(targetContainer.InstanceID) : '');
            setSelectedContainerId(targetContainer.ContainerID ? String(targetContainer.ContainerID) : '');
            setSelectedCredentialId(targetContainer.CredentialID ? String(targetContainer.CredentialID) : '');
            setBindingName(targetContainer.BindingName || (targetContainer.ContainerName ? `${targetContainer.ContainerName} Binding` : ''));
            setBindingNotes(targetContainer.BindingNotes || '');
            setIsActive(true);

            // Do not assume instance level credentials by default; render the contextual triggers
            setCredentialScopeLevel(null);

            const isSaaS = targetContainer.ContainerType === 'SAAS_TENANT';
            setNewCredForm({
                CredentialName: targetContainer.ContainerName ? `${targetContainer.ContainerName} Access Credential` : '',
                SourceTypeID: isSaaS ? 2 : 1,
                AuthModelID: isSaaS ? 2 : 3,
                Username: isSaaS ? '' : 'sa',
                password: '',
                realmId: isSaaS ? (targetContainer.ContainerName || '') : '',
                clientId: '',
                clientSecret: '',
                accessToken: '',
                refreshToken: '',
                environment: 'sandbox',
                baseUrl: isSaaS ? 'https://sandbox-quickbooks.api.intuit.com' : '',
                apiKey: '',
                headerName: 'Authorization',
                domain: '',
                notes: ''
            });
        } else {
            setSelectedOrgId('');
            setSelectedHostId('');
            setSelectedInterfaceId('');
            setSelectedInstanceId('');
            setSelectedContainerId('');
            setSelectedCredentialId('');
            setCredentialScopeLevel(null);
            setBindingName('');
            setBindingNotes('');
            setIsActive(true);
            setNewCredForm({
                CredentialName: '',
                SourceTypeID: 1,
                AuthModelID: 3,
                Username: 'sa',
                password: '',
                realmId: '',
                clientId: '',
                clientSecret: '',
                accessToken: '',
                refreshToken: '',
                environment: 'sandbox',
                baseUrl: '',
                apiKey: '',
                headerName: 'Authorization',
                domain: '',
                notes: ''
            });
        }
    }, [isOpen, editingConnection, targetContainer]);

    // Close on Escape key (The Light Dismiss Rule)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);



    // =========================================================================
    // CASCADING OPTIONS COMPUTATION
    // =========================================================================

    // 0. Organizations
    const orgOptions = useMemo(() => {
        const list = (topology.organizations || []).map(o => ({
            value: String(o.OrganizationID),
            label: o.OrganizationName,
            description: `Type: ${o.OrganizationType || 'Store'}`,
            type: o.OrganizationType || 'ORGANIZATION'
        }));
        return [
            { value: '__ALL__', label: 'All Organizations / Global Infrastructure', description: 'Show hosts across all operational units', type: 'ALL' },
            { value: '__UNASSIGNED__', label: 'Unassigned Host Servers', description: 'Hosts without an explicit organization assignment', type: 'UNASSIGNED' },
            ...list
        ];
    }, [topology.organizations]);

    // 1. Hosts filtered to selected Organization
    const hostOptions = useMemo(() => {
        let list = topology.hosts || [];
        if (selectedOrgId && selectedOrgId !== '__ALL__') {
            if (selectedOrgId === '__UNASSIGNED__') {
                list = list.filter(h => !h.OrganizationID);
            } else {
                list = list.filter(h => String(h.OrganizationID) === String(selectedOrgId));
            }
        }
        return list.map(h => {
            const org = (topology.organizations || []).find(o => o.OrganizationID === h.OrganizationID);
            return {
                value: String(h.HostID),
                label: h.DisplayName || h.HostName,
                description: `${h.HostName} (${h.HostType || 'SERVER'})${org ? ` • ${org.OrganizationName}` : ''}`,
                type: h.HostType || 'SERVER'
            };
        });
    }, [topology.hosts, topology.organizations, selectedOrgId]);

    // 2. Network Interfaces filtered to selected Host
    const availableInterfaces = useMemo(() => {
        if (!selectedHostId) return [];
        return (topology.interfaces || []).filter(i => 
            String(i.DeviceID || i.HostID) === String(selectedHostId) && 
            i.IsActive !== false && i.IsActive !== 0
        );
    }, [topology.interfaces, selectedHostId]);

    const interfaceOptions = useMemo(() => {
        if (!selectedHostId) return [];
        return availableInterfaces.map(iface => {
            const typeStr = iface.TypeCode || iface.TypeName || 'INTERFACE';
            return {
                value: String(iface.InterfaceID),
                label: `${iface.EndpointAddress || iface.IPAddress} [${typeStr}]`,
                description: `Priority ${iface.PriorityOrder || 10}${iface.NetworkIdentifier ? ` • ${iface.NetworkIdentifier}` : ''}${iface.Notes ? ` • ${iface.Notes}` : ''}`,
                isPowered: !!iface.IsOnline,
                type: typeStr
            };
        });
    }, [availableInterfaces, selectedHostId]);

    // 3. Service Instances filtered to selected Host
    const availableInstances = useMemo(() => {
        if (!selectedHostId) return [];
        return (topology.instances || []).filter(i => 
            String(i.HostID) === String(selectedHostId) &&
            i.IsActive !== false && i.IsActive !== 0
        );
    }, [topology.instances, selectedHostId]);

    const instanceOptions = useMemo(() => {
        if (!selectedHostId) return [];
        return availableInstances.map(i => ({
            value: String(i.InstanceID),
            label: `${i.ServiceName} (${i.EngineType}:${i.ListenPort})`,
            description: `${i.EngineType} listening on port ${i.ListenPort}`,
            type: i.EngineType
        }));
    }, [availableInstances, selectedHostId]);

    // 4. Containers filtered to selected Instance (with Live Discovery Hydration)
    const availableContainers = useMemo(() => {
        if (!selectedInstanceId) return [];
        return (topology.containers || []).filter(c => 
            String(c.InstanceID) === String(selectedInstanceId) &&
            c.IsActive !== false && c.IsActive !== 0
        );
    }, [topology.containers, selectedInstanceId]);

    const containerOptions = useMemo(() => {
        if (!selectedInstanceId || !selectedInterfaceId) return [];
        const opts = [
            {
                value: '__INSTANCE__',
                label: '⚡ [Instance-Level Binding]',
                description: 'Apply credentials at service instance level (inherited by all databases on this instance)',
                type: 'INSTANCE'
            }
        ];

        // Dynamic Hydration: If live databases were discovered from the instance, merge them into options!
        if (discoveredDatabases.length > 0) {
            for (const dbName of discoveredDatabases) {
                const existing = availableContainers.find(c => c.ContainerName?.toLowerCase() === dbName.toLowerCase());
                opts.push({
                    value: existing ? String(existing.ContainerID) : `__LIVE__:${dbName}`,
                    label: `${dbName} (Live Server)`,
                    description: existing ? `Matched Catalog Container #${existing.ContainerID}` : 'Discovered from live SQL instance',
                    type: 'RELATIONAL_DATABASE',
                    isLive: true
                });
            }
        } else {
            for (const c of availableContainers) {
                opts.push({
                    value: String(c.ContainerID),
                    label: c.ContainerName,
                    description: `${c.ContainerType || 'DATABASE'} • Role: ${c.ContainerRole || 'PRODUCTION'}`,
                    type: c.ContainerType || 'DATABASE'
                });
            }
        }
        return opts;
    }, [availableContainers, selectedInstanceId, selectedInterfaceId, discoveredDatabases]);

    // Determine current selected objects
    const selectedHost = useMemo(() => {
        return (topology.hosts || []).find(h => String(h.HostID) === String(selectedHostId));
    }, [topology.hosts, selectedHostId]);

    const selectedInstance = useMemo(() => {
        return (topology.instances || []).find(i => String(i.InstanceID) === String(selectedInstanceId));
    }, [topology.instances, selectedInstanceId]);

    const selectedContainer = useMemo(() => {
        if (!selectedContainerId || selectedContainerId === '__INSTANCE__' || selectedContainerId.startsWith('__LIVE__:')) return null;
        return (topology.containers || []).find(c => String(c.ContainerID) === String(selectedContainerId));
    }, [topology.containers, selectedContainerId]);

    const selectedInterface = useMemo(() => {
        return availableInterfaces.find(i => String(i.InterfaceID) === String(selectedInterfaceId));
    }, [availableInterfaces, selectedInterfaceId]);

    // Determine target type: RELATIONAL_DATABASE vs SAAS_TENANT
    const isSaaSTarget = useMemo(() => {
        if (selectedContainer) {
            return selectedContainer.ContainerType === 'SAAS_TENANT';
        }
        if (selectedContainerId === '__INSTANCE__' && selectedInstance) {
            return selectedInstance.EngineType === 'REST_API';
        }
        return false;
    }, [selectedContainer, selectedContainerId, selectedInstance]);

    // 5. Credentials filtered to match the active scope level (Instance vs Container)
    const availableCredentials = useMemo(() => {
        if (credentialScopeLevel === 'INSTANCE') {
            // Strictly exclude non-admin or SaaS credentials that cannot view all databases
            return (topology.credentials || []).filter(c => {
                return (c.AuthModelID === 3 || c.AuthModelID === 5 || [1, 2, 3].includes(c.CredentialTypeID)) && c.SourceTypeID !== 2;
            });
        }
        if (credentialScopeLevel === 'CONTAINER') {
            return (topology.credentials || []).filter(c => {
                if (isSaaSTarget) {
                    return c.AuthModelID === 1 || c.AuthModelID === 2 || c.SourceTypeID === 2 || c.CredentialTypeID === 6;
                } else {
                    return (c.AuthModelID === 3 || c.AuthModelID === 5 || [1, 2, 3].includes(c.CredentialTypeID)) && c.SourceTypeID !== 2;
                }
            });
        }
        return [];
    }, [topology.credentials, credentialScopeLevel, isSaaSTarget]);

    const credentialOptions = useMemo(() => {
        if (!credentialScopeLevel) return [];
        const opts = [
            {
                value: '__NEW__',
                label: `+ [Add New ${isSaaSTarget ? 'OAuth / API' : 'SQL Login'} Credential...]`,
                description: `Create a new credential matching ${isSaaSTarget ? 'SaaS' : 'Database'} authentication`,
                isCustom: true
            }
        ];
        for (const c of availableCredentials) {
            const am = (topology.authModels || []).find(a => a.AuthModelID === c.AuthModelID);
            opts.push({
                value: String(c.CredentialID),
                label: c.CredentialName,
                description: `${am?.AuthModelName || 'Login'}${c.Username ? ` • User: ${c.Username}` : ''}`,
                type: am?.AuthModelCode || 'AUTH'
            });
        }
        return opts;
    }, [availableCredentials, credentialScopeLevel, isSaaSTarget, topology.authModels]);

    // Selected credential object
    const selectedCredObj = useMemo(() => {
        if (!selectedCredentialId || selectedCredentialId === '__NEW__') return null;
        return (topology.credentials || []).find(c => String(c.CredentialID) === String(selectedCredentialId));
    }, [topology.credentials, selectedCredentialId]);

    // Active AuthModel definition
    const activeAuthModel = useMemo(() => {
        if (selectedCredObj) {
            return (topology.authModels || []).find(am => am.AuthModelID === selectedCredObj.AuthModelID);
        }
        return (topology.authModels || []).find(am => am.AuthModelID === parseInt(newCredForm.AuthModelID, 10));
    }, [selectedCredObj, topology.authModels, newCredForm.AuthModelID]);

    const isOAuth = activeAuthModel?.AuthModelCode === 'OAUTH2' || (isSaaSTarget && parseInt(newCredForm.AuthModelID, 10) === 2);
    const isApiKey = activeAuthModel?.AuthModelCode === 'API_KEY' || (isSaaSTarget && parseInt(newCredForm.AuthModelID, 10) === 1);
    const isWindowsAuth = activeAuthModel?.AuthModelCode === 'WINDOWS_AUTH' || activeAuthModel?.AuthModelCode === 'NTLM' || parseInt(newCredForm.AuthModelID, 10) === 5;
    const isSqlLogin = !isOAuth && !isApiKey && !isWindowsAuth;

    // Filter authModels displayed in the new credential subform
    const selectableAuthModels = useMemo(() => {
        return (topology.authModels || []).filter(am => {
            if (isSaaSTarget) {
                return am.AuthModelCode === 'OAUTH2' || am.AuthModelCode === 'API_KEY' || am.AuthModelID === 1 || am.AuthModelID === 2;
            } else {
                return am.AuthModelCode === 'PASSWORD' || am.AuthModelCode === 'WINDOWS_AUTH' || am.AuthModelID === 3 || am.AuthModelID === 5;
            }
        });
    }, [topology.authModels, isSaaSTarget]);

    // Organization info resolution
    const effectiveOrgInfo = useMemo(() => {
        if (selectedContainer?.OrganizationID) {
            const org = topology.organizations.find(o => o.OrganizationID === selectedContainer.OrganizationID);
            return { name: org?.OrganizationName || `Org #${selectedContainer.OrganizationID}`, level: 'Container Override' };
        }
        if (selectedInstance?.OrganizationID) {
            const org = topology.organizations.find(o => o.OrganizationID === selectedInstance.OrganizationID);
            return { name: org?.OrganizationName || `Org #${selectedInstance.OrganizationID}`, level: 'Instance Override' };
        }
        if (selectedHost?.OrganizationID) {
            const org = topology.organizations.find(o => o.OrganizationID === selectedHost.OrganizationID);
            return { name: org?.OrganizationName || `Org #${selectedHost.OrganizationID}`, level: 'Inherited from Host' };
        }
        if (selectedOrgId && selectedOrgId !== '__ALL__' && selectedOrgId !== '__UNASSIGNED__' && selectedOrgId !== '') {
            const org = topology.organizations.find(o => String(o.OrganizationID) === String(selectedOrgId));
            if (org) return { name: org.OrganizationName, level: 'Selected Org' };
        }
        return { name: 'Default Organization', level: 'Unassigned' };
    }, [topology.organizations, selectedContainer, selectedInstance, selectedHost, selectedOrgId]);

    // =========================================================================
    // INLINE ENTITY CREATION HANDLERS
    // =========================================================================

    const handleCreateOrganization = async (e) => {
        if (e) e.preventDefault();
        if (!newOrgForm.OrganizationName.trim()) return;
        try {
            const res = await fetch('/api/catalog/organizations', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(newOrgForm)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const createdOrg = data.data;
                const createdOrgId = String(createdOrg.OrganizationID);
                setTopology(prev => ({
                    ...prev,
                    organizations: [...prev.organizations, createdOrg]
                }));
                setSelectedOrgId(createdOrgId);
                setIsAddingOrg(false);
                setNewOrgForm({ OrganizationName: '', OrganizationType: 'Store', ParentOrganizationID: '' });
                if (onNotification) onNotification(`Created organization "${createdOrg.OrganizationName}"!`);
            } else {
                alert(data.error || 'Failed to create organization');
            }
        } catch (err) {
            alert('Error creating organization: ' + err.message);
        }
    };

    const handleCreateHost = async (e) => {
        if (e) e.preventDefault();
        if (!newHostForm.HostName.trim()) return;
        try {
            const orgIdVal = newHostForm.OrganizationID ? parseInt(newHostForm.OrganizationID, 10) : (selectedOrgId && selectedOrgId !== '__ALL__' && selectedOrgId !== '__UNASSIGNED__' ? parseInt(selectedOrgId, 10) : null);
            const payload = {
                HostName: newHostForm.HostName.trim(),
                DisplayName: newHostForm.DisplayName.trim() || newHostForm.HostName.trim(),
                HostType: newHostForm.HostType || 'ON_PREM_SERVER',
                OrganizationID: orgIdVal
            };
            const res = await fetch('/api/catalog/hosts', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const created = data.data;
                const createdId = String(created.HostID);
                setTopology(prev => ({
                    ...prev,
                    hosts: [...prev.hosts, created]
                }));
                setSelectedHostId(createdId);
                setIsAddingHost(false);
                setIsEditingHost(false);
                if (onNotification) onNotification(`Created host "${created.DisplayName || created.HostName}"!`);
            } else {
                alert(data.error || 'Failed to create host');
            }
        } catch (err) {
            alert('Error creating host: ' + err.message);
        }
    };

    const handleUpdateHost = async (e) => {
        if (e) e.preventDefault();
        if (!selectedHostId || !newHostForm.HostName.trim()) return;
        try {
            const orgIdVal = newHostForm.OrganizationID ? parseInt(newHostForm.OrganizationID, 10) : (selectedOrgId && selectedOrgId !== '__ALL__' && selectedOrgId !== '__UNASSIGNED__' ? parseInt(selectedOrgId, 10) : null);
            const payload = {
                HostName: newHostForm.HostName.trim(),
                DisplayName: newHostForm.DisplayName.trim() || newHostForm.HostName.trim(),
                HostType: newHostForm.HostType || 'ON_PREM_SERVER',
                OrganizationID: orgIdVal
            };
            const res = await fetch(`/api/catalog/hosts/${selectedHostId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const updated = data.data;
                setTopology(prev => ({
                    ...prev,
                    hosts: prev.hosts.map(h => String(h.HostID) === String(selectedHostId) ? updated : h)
                }));
                setIsEditingHost(false);
                if (onNotification) onNotification(`Updated host "${updated.DisplayName || updated.HostName}"!`);
            } else {
                alert(data.error || 'Failed to update host');
            }
        } catch (err) {
            alert('Error updating host: ' + err.message);
        }
    };

    const handleCreateInterface = async (e) => {
        if (e) e.preventDefault();
        if (!selectedHostId || !newInterfaceForm.EndpointAddress.trim()) return;
        try {
            const payload = {
                HostID: parseInt(selectedHostId, 10),
                EndpointAddress: newInterfaceForm.EndpointAddress.trim(),
                InterfaceTypeID: parseInt(newInterfaceForm.InterfaceTypeID, 10) || 3,
                NetworkIdentifier: newInterfaceForm.NetworkIdentifier.trim() || null,
                PriorityOrder: parseInt(newInterfaceForm.PriorityOrder, 10) || 10,
                Notes: newInterfaceForm.Notes.trim() || null
            };
            const res = await fetch('/api/catalog/interfaces', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const created = data.data;
                const createdId = String(created.InterfaceID);
                setTopology(prev => ({
                    ...prev,
                    interfaces: [...prev.interfaces, created]
                }));
                setSelectedInterfaceId(createdId);
                setIsAddingInterface(false);
                setIsEditingInterface(false);
                if (onNotification) onNotification(`Added network interface "${created.EndpointAddress}" to host!`);
            } else {
                alert(data.error || 'Failed to add network interface');
            }
        } catch (err) {
            alert('Error adding interface: ' + err.message);
        }
    };

    const handleUpdateInterface = async (e) => {
        if (e) e.preventDefault();
        if (!selectedInterfaceId || !newInterfaceForm.EndpointAddress.trim()) return;
        try {
            const payload = {
                EndpointAddress: newInterfaceForm.EndpointAddress.trim(),
                InterfaceTypeID: parseInt(newInterfaceForm.InterfaceTypeID, 10) || 3,
                NetworkIdentifier: newInterfaceForm.NetworkIdentifier.trim() || null,
                PriorityOrder: parseInt(newInterfaceForm.PriorityOrder, 10) || 10,
                Notes: newInterfaceForm.Notes.trim() || null
            };
            const res = await fetch(`/api/catalog/interfaces/${selectedInterfaceId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const updated = data.data;
                setTopology(prev => ({
                    ...prev,
                    interfaces: prev.interfaces.map(i => String(i.InterfaceID) === String(selectedInterfaceId) ? updated : i)
                }));
                setIsEditingInterface(false);
                if (onNotification) onNotification(`Updated network interface "${updated.EndpointAddress}"!`);
            } else {
                alert(data.error || 'Failed to update interface');
            }
        } catch (err) {
            alert('Error updating interface: ' + err.message);
        }
    };

    const handleCreateInstance = async (e) => {
        if (e) e.preventDefault();
        if (!selectedHostId || !newInstanceForm.ServiceName.trim()) return;
        try {
            const payload = {
                HostID: parseInt(selectedHostId, 10),
                ServiceName: newInstanceForm.ServiceName.trim(),
                EngineType: newInstanceForm.EngineType || 'SQL Server',
                ListenPort: parseInt(newInstanceForm.ListenPort, 10) || 1433,
                OrganizationID: newInstanceForm.OrganizationID ? parseInt(newInstanceForm.OrganizationID, 10) : null
            };
            const res = await fetch('/api/catalog/instances', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const created = data.data;
                const createdId = String(created.InstanceID);
                setTopology(prev => ({
                    ...prev,
                    instances: [...prev.instances, created]
                }));
                setSelectedInstanceId(createdId);
                setIsAddingInstance(false);
                setIsEditingInstance(false);
                if (onNotification) onNotification(`Created service instance "${created.ServiceName}"!`);
            } else {
                alert(data.error || 'Failed to create instance');
            }
        } catch (err) {
            alert('Error creating instance: ' + err.message);
        }
    };

    const handleUpdateInstance = async (e) => {
        if (e) e.preventDefault();
        if (!selectedInstanceId || !newInstanceForm.ServiceName.trim()) return;
        try {
            const payload = {
                ServiceName: newInstanceForm.ServiceName.trim(),
                EngineType: newInstanceForm.EngineType || 'SQL Server',
                ListenPort: parseInt(newInstanceForm.ListenPort, 10) || 1433,
                OrganizationID: newInstanceForm.OrganizationID ? parseInt(newInstanceForm.OrganizationID, 10) : null
            };
            const res = await fetch(`/api/catalog/instances/${selectedInstanceId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const updated = data.data;
                setTopology(prev => ({
                    ...prev,
                    instances: prev.instances.map(i => String(i.InstanceID) === String(selectedInstanceId) ? updated : i)
                }));
                setIsEditingInstance(false);
                if (onNotification) onNotification(`Updated service instance "${updated.ServiceName}"!`);
            } else {
                alert(data.error || 'Failed to update instance');
            }
        } catch (err) {
            alert('Error updating instance: ' + err.message);
        }
    };

    const handleCreateContainer = async (e) => {
        if (e) e.preventDefault();
        if (!selectedInstanceId || !newContainerForm.ContainerName.trim()) return;
        try {
            const payload = {
                InstanceID: parseInt(selectedInstanceId, 10),
                ContainerName: newContainerForm.ContainerName.trim(),
                ContainerType: newContainerForm.ContainerType || 'RELATIONAL_DATABASE',
                ContainerRole: newContainerForm.ContainerRole || 'PRODUCTION',
                OrganizationID: newContainerForm.OrganizationID ? parseInt(newContainerForm.OrganizationID, 10) : null
            };
            const res = await fetch('/api/catalog/containers', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const created = data.data;
                const createdId = String(created.ContainerID);
                setTopology(prev => ({
                    ...prev,
                    containers: [...prev.containers, created]
                }));
                setSelectedContainerId(createdId);
                setIsAddingContainer(false);
                setIsEditingContainer(false);
                if (onNotification) onNotification(`Created data container "${created.ContainerName}"!`);
            } else {
                alert(data.error || 'Failed to create container');
            }
        } catch (err) {
            alert('Error creating container: ' + err.message);
        }
    };

    const handleUpdateContainer = async (e) => {
        if (e) e.preventDefault();
        if (!selectedContainerId || selectedContainerId === '__INSTANCE__' || !newContainerForm.ContainerName.trim()) return;
        try {
            const payload = {
                ContainerName: newContainerForm.ContainerName.trim(),
                ContainerType: newContainerForm.ContainerType || 'RELATIONAL_DATABASE',
                ContainerRole: newContainerForm.ContainerRole || 'PRODUCTION',
                OrganizationID: newContainerForm.OrganizationID ? parseInt(newContainerForm.OrganizationID, 10) : null
            };
            const res = await fetch(`/api/catalog/containers/${selectedContainerId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const updated = data.data;
                setTopology(prev => ({
                    ...prev,
                    containers: prev.containers.map(c => String(c.ContainerID) === String(selectedContainerId) ? updated : c)
                }));
                setIsEditingContainer(false);
                if (onNotification) onNotification(`Updated data container "${updated.ContainerName}"!`);
            } else {
                alert(data.error || 'Failed to update container');
            }
        } catch (err) {
            alert('Error updating container: ' + err.message);
        }
    };

    const handleCreateCredential = async (e) => {
        if (e) e.preventDefault();
        if (!newCredForm.CredentialName.trim()) return;
        try {
            const payload = {
                CredentialName: newCredForm.CredentialName.trim(),
                SourceTypeID: isSaaSTarget ? 2 : 1,
                AuthModelID: parseInt(newCredForm.AuthModelID, 10),
                Username: newCredForm.Username.trim(),
                password: newCredForm.password,
                EncryptedPassword: newCredForm.password,
                notes: newCredForm.notes.trim() || null
            };

            if (isSaaSTarget) {
                payload.config = {
                    baseUrl: newCredForm.baseUrl,
                    realmId: newCredForm.realmId,
                    clientId: newCredForm.clientId,
                    clientSecret: newCredForm.clientSecret,
                    accessToken: newCredForm.accessToken,
                    refreshToken: newCredForm.refreshToken,
                    apiKey: newCredForm.apiKey,
                    headerName: newCredForm.headerName,
                    domain: newCredForm.domain
                };
            }

            const res = await fetch('/api/catalog/credentials', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const created = data.data;
                const createdId = String(created.CredentialID);
                setTopology(prev => ({
                    ...prev,
                    credentials: [...prev.credentials, created]
                }));
                setSelectedCredentialId(createdId);
                if (credentialScopeLevel === 'INSTANCE') {
                    discoverInstanceTargets(selectedInstanceId, selectedInterfaceId, createdId);
                }
                if (onNotification) onNotification(`Created credential "${created.CredentialName}"!`);
            } else {
                alert(data.error || 'Failed to create credential');
            }
        } catch (err) {
            alert('Error creating credential: ' + err.message);
        }
    };

    const handleUpdateCredential = async (e) => {
        if (e) e.preventDefault();
        if (!selectedCredentialId || selectedCredentialId === '__NEW__') return;
        try {
            const payload = {
                CredentialName: newCredForm.CredentialName.trim(),
                SourceTypeID: isSaaSTarget ? 2 : 1,
                AuthModelID: parseInt(newCredForm.AuthModelID, 10),
                Username: newCredForm.Username.trim(),
                password: newCredForm.password || undefined,
                notes: newCredForm.notes.trim() || null
            };

            if (isSaaSTarget) {
                payload.config = {
                    baseUrl: newCredForm.baseUrl,
                    realmId: newCredForm.realmId,
                    clientId: newCredForm.clientId,
                    clientSecret: newCredForm.clientSecret || undefined,
                    accessToken: newCredForm.accessToken || undefined,
                    refreshToken: newCredForm.refreshToken || undefined,
                    apiKey: newCredForm.apiKey || undefined,
                    headerName: newCredForm.headerName,
                    domain: newCredForm.domain
                };
            }

            const res = await fetch(`/api/catalog/credentials/${selectedCredentialId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success && data.data) {
                const updated = data.data;
                setTopology(prev => ({
                    ...prev,
                    credentials: prev.credentials.map(c => String(c.CredentialID) === String(selectedCredentialId) ? updated : c)
                }));
                setIsEditingCredential(false);
                if (credentialScopeLevel === 'INSTANCE') {
                    discoverInstanceTargets(selectedInstanceId, selectedInterfaceId, selectedCredentialId);
                }
                if (onNotification) onNotification(`Updated credential "${updated.CredentialName}"!`);
            } else {
                alert(data.error || 'Failed to update credential');
            }
        } catch (err) {
            alert('Error updating credential: ' + err.message);
        }
    };

    // =========================================================================
    // TEST CONNECTION
    // =========================================================================
    const handleTest = async () => {
        setTestingConnection(true);
        setTestResult(null);

        try {
            const isNewCred = selectedCredentialId === '__NEW__' || !selectedCredentialId;
            const explicitHost = selectedInterface?.EndpointAddress || selectedHost?.HostName;
            const explicitPort = selectedInstance?.ListenPort || 1433;
            const explicitDb = selectedContainer?.ContainerName || (selectedContainerId?.startsWith('__LIVE__:') ? selectedContainerId.replace('__LIVE__:', '') : 'master');

            const testPayload = {
                HostServer: explicitHost,
                HostPort: explicitPort,
                DatabaseName: explicitDb,
                host: explicitHost,
                port: explicitPort,
                database: explicitDb,
                Username: isNewCred ? newCredForm.Username : (selectedCredObj?.Username || 'sa'),
                EncryptedPassword: isNewCred ? newCredForm.password : '',
                password: isNewCred ? newCredForm.password : '',
                SourceTypeID: isSaaSTarget ? 2 : 1,
                AuthModelID: parseInt(newCredForm.AuthModelID, 10),
                CredentialID: !isNewCred ? parseInt(selectedCredentialId, 10) : undefined,
                ContainerID: selectedContainer?.ContainerID || undefined,
                InstanceID: selectedInstance?.InstanceID || undefined,
                InterfaceID: selectedInterface?.InterfaceID || undefined
            };

            if (isSaaSTarget) {
                testPayload.config = {
                    baseUrl: newCredForm.baseUrl,
                    realmId: newCredForm.realmId,
                    clientId: newCredForm.clientId,
                    clientSecret: newCredForm.clientSecret,
                    accessToken: newCredForm.accessToken,
                    refreshToken: newCredForm.refreshToken,
                    apiKey: newCredForm.apiKey,
                    headerName: newCredForm.headerName,
                    domain: newCredForm.domain
                };
            }

            const url = selectedCredentialId && selectedCredentialId !== '__NEW__' 
                ? `/api/catalog/connections/${selectedCredentialId}/test`
                : '/api/catalog/connections/test';

            const res = await fetch(url, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(testPayload)
            });
            const data = await res.json();
            setTestResult(data);
            if (onNotification) {
                onNotification(
                    data.success ? `Connection verified! (${data.latencyMs || data.latency || 0}ms)` : `Connection test failed: ${data.message || data.error || 'Unreachable'}`,
                    data.success ? 'success' : 'error'
                );
            }
        } catch (err) {
            setTestResult({ success: false, message: err.message });
            if (onNotification) {
                onNotification(`Connection test error: ${err.message}`, 'error');
            }
        } finally {
            setTestingConnection(false);
        }
    };

    // =========================================================================
    // SAVE / SUBMIT BINDING
    // =========================================================================
    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const isEdit = !!editingConnection;
            const isNewCred = selectedCredentialId === '__NEW__' || !selectedCredentialId;
            const isInstanceBinding = credentialScopeLevel === 'INSTANCE' || selectedContainerId === '__INSTANCE__' || (!selectedContainerId && selectedInstanceId);
            
            let targetContId = isInstanceBinding ? null : ((selectedContainerId === '__INSTANCE__' || !selectedContainerId) ? null : parseInt(selectedContainerId, 10));
            const targetInstId = selectedInstanceId ? parseInt(selectedInstanceId, 10) : null;

            // Handle live discovered container registration on the fly
            if (!isInstanceBinding && selectedContainerId && selectedContainerId.startsWith('__LIVE__:')) {
                const liveDbName = selectedContainerId.replace('__LIVE__:', '');
                const createContRes = await fetch('/api/catalog/containers', {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify({
                        InstanceID: targetInstId,
                        ContainerName: liveDbName,
                        ContainerType: 'RELATIONAL_DATABASE',
                        ContainerRole: 'PRODUCTION'
                    })
                });
                const createContData = await createContRes.json();
                if (createContData.success && createContData.data?.ContainerID) {
                    targetContId = createContData.data.ContainerID;
                }
            }

            if (!targetContId && !targetInstId) {
                alert('Please select a Target Data Container or Service Instance.');
                setSaving(false);
                return;
            }

            const payload = {
                ContainerID: isInstanceBinding ? null : targetContId,
                InstanceID: targetInstId,
                HostID: selectedHostId ? parseInt(selectedHostId, 10) : null,
                InterfaceID: selectedInterfaceId ? parseInt(selectedInterfaceId, 10) : null,
                CredentialID: !isNewCred ? parseInt(selectedCredentialId, 10) : null,
                // If editing an instance-level connection, but user has targeted a specific container,
                // do not overwrite the instance binding! Clear BindingID so a container-level binding is created.
                BindingID: (editingConnection?.isInheritedFromInstance && targetContId !== null)
                    ? null
                    : (editingConnection?.BindingID || null),
                BindingName: bindingName.trim() || newCredForm.CredentialName || (isInstanceBinding ? 'Instance Binding' : 'Container Binding'),
                BindingNotes: bindingNotes.trim() || null,
                IsActive: isActive,
                newCredential: isNewCred,
                CredentialName: newCredForm.CredentialName.trim() || bindingName.trim() || 'New Credential',
                SourceTypeID: isSaaSTarget ? 2 : 1,
                AuthModelID: parseInt(newCredForm.AuthModelID, 10),
                Username: newCredForm.Username,
                password: newCredForm.password,
                EncryptedPassword: newCredForm.password
            };

            if (isSaaSTarget) {
                payload.config = {
                    baseUrl: newCredForm.baseUrl,
                    realmId: newCredForm.realmId,
                    clientId: newCredForm.clientId,
                    clientSecret: newCredForm.clientSecret,
                    accessToken: newCredForm.accessToken,
                    refreshToken: newCredForm.refreshToken,
                    apiKey: newCredForm.apiKey,
                    headerName: newCredForm.headerName,
                    domain: newCredForm.domain
                };
            }

            const url = isEdit && editingConnection?.CredentialID && !isNewCred
                ? `/api/catalog/connections/${editingConnection.CredentialID}`
                : (targetContId 
                    ? `/api/catalog/containers/${targetContId}/bind`
                    : (targetInstId
                        ? `/api/catalog/instances/${targetInstId}/bind`
                        : '/api/catalog/connections'));

            const method = (isEdit && editingConnection?.CredentialID && !isNewCred) ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: authHeaders,
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (!res.ok || !data.success) {
                alert(data.error || 'Failed to save connection binding');
                setSaving(false);
                return;
            }

            if (data.verified === false) {
                alert('Warning: Server reported that the changes could not be verified in the database.');
                setSaving(false);
                return;
            }

            if (onNotification) onNotification(`Connection binding saved and verified successfully!`);
            onSaveSuccess();
            onClose();
        } catch (err) {
            alert('Error saving connection: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    // =========================================================================
    // CREDENTIAL SUB-SECTION RENDERER (INHERITABLE / HIERARCHICAL)
    // =========================================================================
    const renderCredentialSubSection = (scopeLevel) => {
        const isInstance = scopeLevel === 'INSTANCE';
        const titleColor = isInstance ? 'text-amber-400' : 'text-teal-400';
        const borderColor = isInstance ? 'border-amber-500/50 bg-amber-950/20' : 'border-teal-500/50 bg-teal-950/20';

        return (
            <div className={`p-2.5 rounded-lg border ${borderColor} space-y-2.5 animate-fade-in my-2 shadow-sm`}>
                <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                    <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[14px] text-amber-400">key</span>
                        <h5 className={`text-[11px] font-bold ${titleColor} uppercase tracking-wider`}>
                            {isInstance ? 'Service Instance Credentials' : 'Target Container Credentials'}
                        </h5>
                    </div>
                    <div className="flex items-center gap-2">
                        {isInstance && discoveringDatabases && (
                            <span className="text-[10px] text-amber-300 flex items-center gap-1 font-sans animate-pulse">
                                <span className="material-symbols-outlined text-[12px] animate-spin">sync</span>
                                Probing Instance...
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                setCredentialScopeLevel(null);
                                setSelectedCredentialId('');
                                setDiscoveredDatabases([]);
                            }}
                            className="p-1 rounded text-slate-400 hover:text-rose-400 hover:bg-slate-800 transition-colors"
                            title="Remove / Clear Credential at this level"
                        >
                            <span className="material-symbols-outlined text-[14px]">close</span>
                        </button>
                    </div>
                </div>

                {/* Dropdown Selector */}
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="text-slate-300 font-medium text-[11px]">
                            {isInstance ? 'Instance Admin Login (sa / Windows Auth)' : (isSaaSTarget ? 'API / OAuth Credential' : 'Database Login Credential')}
                        </label>
                        <div className="flex items-center gap-1">
                            {selectedCredentialId && selectedCredentialId !== '__NEW__' && !isEditingCredential && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (selectedCredObj) {
                                            setNewCredForm({
                                                CredentialName: selectedCredObj.CredentialName || '',
                                                SourceTypeID: isSaaSTarget ? 2 : 1,
                                                AuthModelID: selectedCredObj.AuthModelID || (isSaaSTarget ? 2 : 3),
                                                Username: selectedCredObj.Username || '',
                                                password: '',
                                                realmId: isSaaSTarget ? (selectedCredObj.realmId || '') : '',
                                                clientId: isSaaSTarget ? (selectedCredObj.clientId || '') : '',
                                                clientSecret: '',
                                                accessToken: '',
                                                refreshToken: '',
                                                environment: selectedCredObj.environment || 'sandbox',
                                                baseUrl: isSaaSTarget ? (selectedCredObj.baseUrl || '') : '',
                                                apiKey: '',
                                                headerName: 'Authorization',
                                                domain: selectedCredObj.rawConfig?.domain || '',
                                                notes: selectedCredObj.Notes || ''
                                            });
                                            setIsEditingCredential(true);
                                        }
                                    }}
                                    className="p-1 text-sky-400 hover:text-sky-300 rounded hover:bg-slate-800 transition-colors"
                                    title="Edit Selected Credential"
                                >
                                    <span className="material-symbols-outlined text-[13px]">edit</span>
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    if (selectedCredentialId === '__NEW__') {
                                        setSelectedCredentialId('');
                                    } else {
                                        setSelectedCredentialId('__NEW__');
                                        setIsEditingCredential(false);
                                    }
                                }}
                                className="p-1 text-emerald-400 hover:text-emerald-300 rounded hover:bg-slate-800 transition-colors"
                                title={selectedCredentialId === '__NEW__' ? 'Cancel' : 'Add New Credential'}
                            >
                                <span className="material-symbols-outlined text-[14px]">{selectedCredentialId === '__NEW__' ? 'remove' : 'add'}</span>
                            </button>
                        </div>
                    </div>

                    <SearchableSelect
                        options={credentialOptions}
                        value={selectedCredentialId}
                        onChange={(val) => {
                            setSelectedCredentialId(val);
                            if (isInstance && val && val !== '__NEW__') {
                                discoverInstanceTargets(selectedInstanceId, selectedInterfaceId, val);
                            }
                        }}
                        placeholder={`Choose ${isInstance ? 'Instance' : 'Container'} Credential ...`}
                        className="w-full font-mono"
                    />
                </div>

                {/* Instance Discovery Hydration Status */}
                {isInstance && (
                    <>
                        {discoveringDatabases && (
                            <div className="text-[10px] text-amber-300 flex items-center gap-1.5 font-sans py-1 animate-pulse">
                                <span className="material-symbols-outlined text-[13px] animate-spin">sync</span>
                                Connecting to SQL instance to enumerate live online databases...
                            </div>
                        )}
                        {!discoveringDatabases && discoveredDatabases.length > 0 && (
                            <div className="text-[10px] text-emerald-400 flex items-center justify-between font-sans py-1 px-2 bg-emerald-950/60 border border-emerald-800/60 rounded">
                                <span className="flex items-center gap-1 font-semibold">
                                    <span className="material-symbols-outlined text-[13px]">check_circle</span>
                                    {discoveredDatabases.length} Live Databases Discovered &amp; Hydrated Below
                                </span>
                                <button
                                    type="button"
                                    onClick={() => discoverInstanceTargets(selectedInstanceId, selectedInterfaceId, selectedCredentialId)}
                                    className="p-1 text-emerald-300 hover:text-white rounded hover:bg-slate-800 transition-colors"
                                    title="Re-probe Live Databases"
                                >
                                    <span className="material-symbols-outlined text-[13px]">refresh</span>
                                </button>
                            </div>
                        )}
                        {instanceDiscoverError && (
                            <div className="text-[10px] text-rose-400 flex items-center gap-1 font-sans py-1">
                                <span className="material-symbols-outlined text-[13px]">warning</span>
                                {instanceDiscoverError}
                            </div>
                        )}
                    </>
                )}

                {/* Inline Credential Form (Expanded ONLY when creating new or editing) */}
                {(selectedCredentialId === '__NEW__' || isEditingCredential) && (
                    <div className="p-2.5 bg-slate-900/80 rounded border border-slate-700/80 space-y-2 text-xs animate-fade-in">
                        <div className="flex items-center justify-between pb-1 border-b border-slate-700/60">
                            <span className="text-[10px] font-bold text-teal-300">
                                {isEditingCredential ? 'Edit Credential Details' : 'Create New Security Credential'}
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditingCredential(false);
                                    if (selectedCredentialId === '__NEW__') setSelectedCredentialId('');
                                }}
                                className="p-0.5 text-slate-400 hover:text-white"
                                title="Close Form"
                            >
                                <span className="material-symbols-outlined text-[14px]">close</span>
                            </button>
                        </div>

                        <div>
                            <label className="text-[10px] text-slate-400 block mb-0.5">Credential Label / Name</label>
                            <input
                                type="text"
                                required
                                value={newCredForm.CredentialName}
                                onChange={e => setNewCredForm({ ...newCredForm, CredentialName: e.target.value })}
                                placeholder="e.g. Production SQL sa / Master Login"
                                className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[10px] text-slate-400 block mb-0.5">Authentication Model</label>
                                <select
                                    value={newCredForm.AuthModelID}
                                    onChange={e => setNewCredForm({ ...newCredForm, AuthModelID: parseInt(e.target.value, 10) })}
                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                >
                                    {selectableAuthModels.map(am => (
                                        <option key={am.AuthModelID} value={am.AuthModelID}>
                                            {am.AuthModelName} ({am.AuthModelCode})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] text-slate-400 block mb-0.5">Username / Login ID</label>
                                <input
                                    type="text"
                                    value={newCredForm.Username}
                                    onChange={e => setNewCredForm({ ...newCredForm, Username: e.target.value })}
                                    placeholder={isSqlLogin ? 'sa' : 'Username / Client'}
                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs font-mono"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] text-slate-400 block mb-0.5">Password / Secret</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={newCredForm.password}
                                    onChange={e => setNewCredForm({ ...newCredForm, password: e.target.value })}
                                    placeholder={isEditingCredential ? '(Leave blank to retain existing encrypted password)' : 'Secret password...'}
                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 pr-8 text-white text-xs font-mono"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white p-0.5"
                                    title={showPassword ? 'Hide Password' : 'Show Password'}
                                >
                                    <span className="material-symbols-outlined text-[13px]">{showPassword ? 'visibility_off' : 'visibility'}</span>
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] text-slate-400 block mb-0.5">Credential Notes (Optional)</label>
                            <input
                                type="text"
                                value={newCredForm.notes}
                                onChange={e => setNewCredForm({ ...newCredForm, notes: e.target.value })}
                                placeholder="Administrative purpose..."
                                className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                            />
                        </div>

                        <div className="flex justify-end items-center gap-2 pt-1 border-t border-slate-800/80">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditingCredential(false);
                                    if (selectedCredentialId === '__NEW__') setSelectedCredentialId('');
                                }}
                                className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors text-xs"
                                title="Cancel"
                            >
                                <span className="material-symbols-outlined text-[14px]">close</span>
                            </button>
                            <button
                                type="button"
                                onClick={isEditingCredential ? handleUpdateCredential : handleCreateCredential}
                                className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-semibold flex items-center gap-1 shadow transition-colors"
                                title="Save Credential"
                            >
                                <span className="material-symbols-outlined text-[14px]">save</span>
                                <span>{isEditingCredential ? 'Update Credential' : 'Save Credential'}</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden flex justify-end bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose}>
            <div 
                className="w-full max-w-xl h-full bg-slate-950 border-l border-slate-800 shadow-2xl flex flex-col z-50 overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-4 py-2.5 bg-slate-900 border-b border-slate-800 sticky top-0 z-20 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-emerald-400 text-lg">hub</span>
                        <div>
                            <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                                {editingConnection ? 'Edit Connection & Binding' : (targetContainer ? `Bind Credential to ${targetContainer.ContainerName}` : 'New Connection Binding')}
                            </h3>
                            <p className="text-[10px] text-slate-400">
                                Cascading target infrastructure (Org &rarr; Host &rarr; Interface & Instance &rarr; Container) and security credentials.
                            </p>
                        </div>
                    </div>
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                        title="Close (Esc)"
                    >
                        <span className="material-symbols-outlined text-base">close</span>
                    </button>
                </div>

                {loadingTopology ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 text-slate-400 gap-2">
                        <span className="material-symbols-outlined animate-spin text-2xl text-emerald-400">progress_activity</span>
                        <span className="text-xs">Loading infrastructure topology...</span>
                    </div>
                ) : (
                    <form onSubmit={handleSave} className="flex-1 p-3.5 flex flex-col gap-3 text-xs text-slate-300">

                        {/* ========================================================================= */}
                        {/* SECTION 1: BINDING CONFIGURATION (STRICT EXPLICIT ROUTING - NO DEFAULT REGRESSION) */}
                        {/* ========================================================================= */}
                        <div className="border-b border-slate-800/80 pb-3 space-y-2">
                            <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                <h4 className="text-[11px] font-bold text-amber-400 flex items-center gap-1.5 uppercase tracking-wider">
                                    <span className="material-symbols-outlined text-[14px]">link</span>
                                    1. Binding Configuration
                                </h4>
                                <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-slate-300 hover:text-white">
                                    <input
                                        type="checkbox"
                                        checked={isActive}
                                        onChange={e => setIsActive(e.target.checked)}
                                        className="rounded bg-slate-900 border-slate-700 text-emerald-500 focus:ring-0 w-3.5 h-3.5"
                                    />
                                    <span>Active</span>
                                </label>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[10px] text-slate-400 block mb-0.5">Binding Name / Label</label>
                                    <input
                                        type="text"
                                        value={bindingName}
                                        onChange={e => setBindingName(e.target.value)}
                                        placeholder="e.g. Primary Production Binding"
                                        className="w-full bg-slate-900/90 border border-slate-700/80 rounded px-2 py-1 text-xs text-white"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] text-slate-400 block mb-0.5">Notes / Purpose</label>
                                    <input
                                        type="text"
                                        value={bindingNotes}
                                        onChange={e => setBindingNotes(e.target.value)}
                                        placeholder="Optional operational purpose..."
                                        className="w-full bg-slate-900/90 border border-slate-700/80 rounded px-2 py-1 text-xs text-white"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ========================================================================= */}
                        {/* SECTION 2: TARGET INFRASTRUCTURE (PROGRESSIVE CASCADING & NESTING) */}
                        {/* ========================================================================= */}
                        <div className="border-b border-slate-800/80 pb-3 space-y-3">
                            <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                <h4 className="text-[11px] font-bold text-emerald-400 flex items-center gap-1.5 uppercase tracking-wider">
                                    <span className="material-symbols-outlined text-[14px]">account_tree</span>
                                    2. Target Infrastructure Hierarchy
                                </h4>
                                {effectiveOrgInfo.name && (
                                    <div className="text-[10px] flex items-center gap-1 text-slate-400">
                                        <span>Org:</span>
                                        <span className="text-white font-semibold">{effectiveOrgInfo.name}</span>
                                        <span className="text-[9px] text-emerald-400/90 bg-emerald-950/60 px-1 py-0.2 rounded border border-emerald-800/40">
                                            {effectiveOrgInfo.level}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* 2.0 Organization (Root Level 0) */}
                            <div className="pl-0">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-slate-300 font-medium text-[11px] flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[13px] text-purple-400">corporate_fare</span>
                                        Organization
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsAddingOrg(!isAddingOrg);
                                            setNewOrgForm({ OrganizationName: '', OrganizationType: 'Store', ParentOrganizationID: '' });
                                        }}
                                        className="p-1 rounded text-emerald-400 hover:text-emerald-300 hover:bg-slate-800 transition-colors"
                                        title={isAddingOrg ? 'Cancel' : 'Add New Organization'}
                                    >
                                        <span className="material-symbols-outlined text-[14px]">{isAddingOrg ? 'close' : 'add'}</span>
                                    </button>
                                </div>

                                {isAddingOrg ? (
                                    <div className="p-2.5 bg-slate-900/80 rounded border border-purple-900/60 space-y-2 animate-fade-in">
                                        <div className="flex items-center justify-between pb-1 border-b border-purple-900/40">
                                            <span className="text-[10px] font-bold text-purple-300">Add New Organization</span>
                                            <button type="button" onClick={() => setIsAddingOrg(false)} className="p-0.5 text-slate-400 hover:text-white" title="Cancel">
                                                <span className="material-symbols-outlined text-[13px]">close</span>
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Organization Name</label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={newOrgForm.OrganizationName}
                                                    onChange={e => setNewOrgForm({ ...newOrgForm, OrganizationName: e.target.value })}
                                                    placeholder="e.g. Pittsford Store"
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-slate-400 block mb-0.5">Organization Type</label>
                                                <select
                                                    value={newOrgForm.OrganizationType}
                                                    onChange={e => setNewOrgForm({ ...newOrgForm, OrganizationType: e.target.value })}
                                                    className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                >
                                                    <option value="Store">Store</option>
                                                    <option value="Franchise">Franchise</option>
                                                    <option value="Corporate">Corporate</option>
                                                    <option value="Internal">Internal</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex justify-end items-center gap-2 pt-1">
                                            <button
                                                type="button"
                                                onClick={() => setIsAddingOrg(false)}
                                                className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 text-xs"
                                                title="Cancel"
                                            >
                                                <span className="material-symbols-outlined text-[13px]">close</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleCreateOrganization}
                                                className="px-2.5 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded text-[11px] font-semibold flex items-center gap-1 shadow transition-colors"
                                                title="Save Organization"
                                            >
                                                <span className="material-symbols-outlined text-[13px]">save</span>
                                                Save Organization
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <SearchableSelect
                                        options={orgOptions}
                                        value={selectedOrgId}
                                        onChange={(val) => {
                                            setSelectedOrgId(val);
                                            setSelectedHostId('');
                                            setSelectedInterfaceId('');
                                            setSelectedInstanceId('');
                                            setSelectedContainerId('');
                                            setSelectedCredentialId('');
                                            setCredentialScopeLevel(null);
                                            setDiscoveredDatabases([]);
                                        }}
                                        placeholder="Choose Organization ..."
                                        className="w-full"
                                    />
                                )}
                            </div>

                            {/* 2.1 Host Server / Device (Nested Level 1 - Indented) */}
                            {selectedOrgId ? (
                                <div className="pl-3 border-l-2 border-purple-500/30 space-y-1 animate-fade-in pt-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-slate-300 font-medium text-[11px] flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[13px] text-emerald-400">dns</span>
                                            Host Server / Device
                                        </label>
                                        <div className="flex items-center gap-1">
                                            {selectedHostId && !isAddingHost && !isEditingHost && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const h = topology.hosts.find(x => String(x.HostID) === String(selectedHostId));
                                                        if (h) {
                                                            setNewHostForm({
                                                                HostName: h.HostName || '',
                                                                DisplayName: h.DisplayName || '',
                                                                HostType: h.HostType || 'ON_PREM_SERVER',
                                                                OrganizationID: h.OrganizationID ? String(h.OrganizationID) : (selectedOrgId !== '__ALL__' && selectedOrgId !== '__UNASSIGNED__' ? selectedOrgId : '')
                                                            });
                                                            setIsAddingHost(false);
                                                            setIsEditingHost(true);
                                                        }
                                                    }}
                                                    className="p-1 rounded text-sky-400 hover:text-sky-300 hover:bg-slate-800 transition-colors"
                                                    title="Edit Selected Host"
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">edit</span>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (isAddingHost || isEditingHost) {
                                                        setIsAddingHost(false);
                                                        setIsEditingHost(false);
                                                    } else {
                                                        setIsAddingHost(true);
                                                        setIsEditingHost(false);
                                                        setNewHostForm({ 
                                                            HostName: '', 
                                                            DisplayName: '', 
                                                            HostType: 'ON_PREM_SERVER', 
                                                            OrganizationID: selectedOrgId !== '__ALL__' && selectedOrgId !== '__UNASSIGNED__' ? selectedOrgId : '' 
                                                        });
                                                    }
                                                }}
                                                className="p-1 rounded text-emerald-400 hover:text-emerald-300 hover:bg-slate-800 transition-colors"
                                                title={(isAddingHost || isEditingHost) ? 'Cancel' : 'Add New Host'}
                                            >
                                                <span className="material-symbols-outlined text-[14px]">{(isAddingHost || isEditingHost) ? 'close' : 'add'}</span>
                                            </button>
                                        </div>
                                    </div>

                                    {(isAddingHost || isEditingHost) ? (
                                        <div className="p-2.5 bg-slate-900/60 rounded border border-slate-800 space-y-2 animate-fade-in">
                                            <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                                <span className="text-[10px] font-bold text-emerald-300">
                                                    {isEditingHost ? 'Edit Host Server' : 'Add New Host Server'}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => { setIsAddingHost(false); setIsEditingHost(false); }}
                                                    className="p-0.5 text-slate-400 hover:text-white"
                                                    title="Cancel"
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">close</span>
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Host Name (DNS / NetBIOS)</label>
                                                    <input
                                                        type="text"
                                                        required
                                                        value={newHostForm.HostName}
                                                        onChange={e => setNewHostForm({ ...newHostForm, HostName: e.target.value })}
                                                        placeholder="e.g. MIDSVR"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs font-mono"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Display Name</label>
                                                    <input
                                                        type="text"
                                                        value={newHostForm.DisplayName}
                                                        onChange={e => setNewHostForm({ ...newHostForm, DisplayName: e.target.value })}
                                                        placeholder="e.g. Midland Server"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-end items-center gap-2 pt-1">
                                                <button
                                                    type="button"
                                                    onClick={() => { setIsAddingHost(false); setIsEditingHost(false); }}
                                                    className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 text-xs"
                                                    title="Cancel"
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">close</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={isEditingHost ? handleUpdateHost : handleCreateHost}
                                                    className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-[11px] font-semibold flex items-center gap-1 shadow transition-colors"
                                                    title="Save Host"
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">save</span>
                                                    <span>{isEditingHost ? 'Update Host' : 'Save Host'}</span>
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <SearchableSelect
                                            options={hostOptions}
                                            value={selectedHostId}
                                            onChange={(val) => {
                                                setSelectedHostId(val);
                                                setSelectedInterfaceId('');
                                                setSelectedInstanceId('');
                                                setSelectedContainerId('');
                                                setSelectedCredentialId('');
                                                setCredentialScopeLevel(null);
                                                setDiscoveredDatabases([]);
                                            }}
                                            placeholder="Choose Host Server / Device ..."
                                            className="w-full"
                                        />
                                    )}
                                </div>
                            ) : null}

                            {/* 2.2 Interface & Instance (Nested Level 2 - Indented Further) */}
                            {selectedHostId ? (
                                <div className="pl-6 border-l-2 border-emerald-500/30 space-y-3 pt-1 animate-fade-in">
                                    
                                    {/* 2.2.1 Network Interface */}
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="text-slate-300 font-medium text-[11px] flex items-center gap-1">
                                                <span className="material-symbols-outlined text-[13px] text-sky-400">lan</span>
                                                Network Interface / Explicit Route
                                            </label>
                                            <div className="flex items-center gap-1">
                                                {selectedInterfaceId && !isAddingInterface && !isEditingInterface && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const iface = availableInterfaces.find(x => String(x.InterfaceID) === String(selectedInterfaceId));
                                                            if (iface) {
                                                                setNewInterfaceForm({
                                                                    EndpointAddress: iface.EndpointAddress || iface.IPAddress || '',
                                                                    InterfaceTypeID: iface.InterfaceTypeID || 3,
                                                                    NetworkIdentifier: iface.NetworkIdentifier || '',
                                                                    PriorityOrder: iface.PriorityOrder || 10,
                                                                    Notes: iface.Notes || ''
                                                                });
                                                                setIsAddingInterface(false);
                                                                setIsEditingInterface(true);
                                                            }
                                                        }}
                                                        className="p-1 rounded text-sky-400 hover:text-sky-300 hover:bg-slate-800 transition-colors"
                                                        title="Edit Selected Interface"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">edit</span>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (isAddingInterface || isEditingInterface) {
                                                            setIsAddingInterface(false);
                                                            setIsEditingInterface(false);
                                                        } else {
                                                            setIsAddingInterface(true);
                                                            setIsEditingInterface(false);
                                                            setNewInterfaceForm({ EndpointAddress: '', InterfaceTypeID: 3, NetworkIdentifier: '', PriorityOrder: 10, Notes: '' });
                                                        }
                                                    }}
                                                    className="p-1 rounded text-emerald-400 hover:text-emerald-300 hover:bg-slate-800 transition-colors"
                                                    title={(isAddingInterface || isEditingInterface) ? 'Cancel' : 'Add Interface'}
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">{(isAddingInterface || isEditingInterface) ? 'close' : 'add'}</span>
                                                </button>
                                            </div>
                                        </div>

                                        {(isAddingInterface || isEditingInterface) ? (
                                            <div className="p-2.5 bg-slate-900/60 rounded border border-slate-800 space-y-2 animate-fade-in">
                                                <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                                    <span className="text-[10px] font-bold text-sky-300">
                                                        {isEditingInterface ? 'Edit Network Interface' : 'Add Network Interface to Host'}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setIsAddingInterface(false); setIsEditingInterface(false); }}
                                                        className="p-0.5 text-slate-400 hover:text-white"
                                                        title="Cancel"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">close</span>
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="text-[10px] text-slate-400 block mb-0.5">Endpoint Address (IP / Host)</label>
                                                        <input
                                                            type="text"
                                                            required
                                                            value={newInterfaceForm.EndpointAddress}
                                                            onChange={e => setNewInterfaceForm({ ...newInterfaceForm, EndpointAddress: e.target.value })}
                                                            placeholder="e.g. 10.0.10.16"
                                                            className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs font-mono"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] text-slate-400 block mb-0.5">Interface Type</label>
                                                        <select
                                                            value={newInterfaceForm.InterfaceTypeID}
                                                            onChange={e => setNewInterfaceForm({ ...newInterfaceForm, InterfaceTypeID: parseInt(e.target.value, 10) })}
                                                            className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                        >
                                                            {(topology.interfaceTypes || []).map(it => (
                                                                <option key={it.InterfaceTypeID} value={it.InterfaceTypeID}>
                                                                    {it.TypeName} ({it.TypeCode})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="flex justify-end items-center gap-2 pt-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setIsAddingInterface(false); setIsEditingInterface(false); }}
                                                        className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 text-xs"
                                                        title="Cancel"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">close</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={isEditingInterface ? handleUpdateInterface : handleCreateInterface}
                                                        className="px-2.5 py-1 bg-sky-600 hover:bg-sky-500 text-white rounded text-[11px] font-semibold flex items-center gap-1 shadow transition-colors"
                                                        title="Save Interface"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">save</span>
                                                        <span>{isEditingInterface ? 'Update Interface' : 'Save Interface'}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <SearchableSelect
                                                options={interfaceOptions}
                                                value={selectedInterfaceId}
                                                onChange={(val) => {
                                                    setSelectedInterfaceId(val);
                                                    if (credentialScopeLevel === 'INSTANCE' && selectedCredentialId) {
                                                        discoverInstanceTargets(selectedInstanceId, val, selectedCredentialId);
                                                    }
                                                }}
                                                placeholder={interfaceOptions.length > 0 ? "Choose Network Interface ..." : "No interfaces configured (+ Add New)"}
                                                disabled={interfaceOptions.length === 0}
                                                className="w-full font-mono"
                                            />
                                        )}
                                    </div>

                                    {/* 2.2.2 Service Instance */}
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="text-slate-300 font-medium text-[11px] flex items-center gap-1">
                                                <span className="material-symbols-outlined text-[13px] text-emerald-400">storage</span>
                                                Service Instance (Database / App Engine)
                                            </label>
                                            <div className="flex items-center gap-1">
                                                {selectedInstanceId && !isAddingInstance && !isEditingInstance && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const inst = topology.instances.find(x => String(x.InstanceID) === String(selectedInstanceId));
                                                            if (inst) {
                                                                setNewInstanceForm({
                                                                    ServiceName: inst.ServiceName || '',
                                                                    EngineType: inst.EngineType || 'SQL Server',
                                                                    ListenPort: inst.ListenPort || 1433,
                                                                    OrganizationID: inst.OrganizationID ? String(inst.OrganizationID) : ''
                                                                });
                                                                setIsAddingInstance(false);
                                                                setIsEditingInstance(true);
                                                            }
                                                        }}
                                                        className="p-1 rounded text-sky-400 hover:text-sky-300 hover:bg-slate-800 transition-colors"
                                                        title="Edit Selected Instance"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">edit</span>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (isAddingInstance || isEditingInstance) {
                                                            setIsAddingInstance(false);
                                                            setIsEditingInstance(false);
                                                        } else {
                                                            setIsAddingInstance(true);
                                                            setIsEditingInstance(false);
                                                            setNewInstanceForm({ ServiceName: '', EngineType: 'SQL Server', ListenPort: 1433, OrganizationID: '' });
                                                        }
                                                    }}
                                                    className="p-1 rounded text-emerald-400 hover:text-emerald-300 hover:bg-slate-800 transition-colors"
                                                    title={(isAddingInstance || isEditingInstance) ? 'Cancel' : 'Add Instance'}
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">{(isAddingInstance || isEditingInstance) ? 'close' : 'add'}</span>
                                                </button>
                                            </div>
                                        </div>

                                        {(isAddingInstance || isEditingInstance) ? (
                                            <div className="p-2.5 bg-slate-900/60 rounded border border-slate-800 space-y-2 animate-fade-in">
                                                <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                                    <span className="text-[10px] font-bold text-emerald-300">
                                                        {isEditingInstance ? 'Edit Service Instance' : 'Add New Service Instance'}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setIsAddingInstance(false); setIsEditingInstance(false); }}
                                                        className="p-0.5 text-slate-400 hover:text-white"
                                                        title="Cancel"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">close</span>
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="col-span-1">
                                                        <label className="text-[10px] text-slate-400 block mb-0.5">Service Name</label>
                                                        <input
                                                            type="text"
                                                            required
                                                            value={newInstanceForm.ServiceName}
                                                            onChange={e => setNewInstanceForm({ ...newInstanceForm, ServiceName: e.target.value })}
                                                            placeholder="e.g. PBSSQL"
                                                            className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs font-mono"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] text-slate-400 block mb-0.5">Engine Type</label>
                                                        <select
                                                            value={newInstanceForm.EngineType}
                                                            onChange={e => setNewInstanceForm({ ...newInstanceForm, EngineType: e.target.value })}
                                                            className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                        >
                                                            <option value="SQL Server">SQL Server</option>
                                                            <option value="REST_API">REST API / SaaS</option>
                                                            <option value="PostgreSQL">PostgreSQL</option>
                                                            <option value="MySQL">MySQL</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] text-slate-400 block mb-0.5">Listen Port</label>
                                                        <input
                                                            type="number"
                                                            value={newInstanceForm.ListenPort}
                                                            onChange={e => setNewInstanceForm({ ...newInstanceForm, ListenPort: parseInt(e.target.value, 10) || 1433 })}
                                                            className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs font-mono"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex justify-end items-center gap-2 pt-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setIsAddingInstance(false); setIsEditingInstance(false); }}
                                                        className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 text-xs"
                                                        title="Cancel"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">close</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={isEditingInstance ? handleUpdateInstance : handleCreateInstance}
                                                        className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-[11px] font-semibold flex items-center gap-1 shadow transition-colors"
                                                        title="Save Instance"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px]">save</span>
                                                        <span>{isEditingInstance ? 'Update Instance' : 'Save Instance'}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <SearchableSelect
                                                options={instanceOptions}
                                                value={selectedInstanceId}
                                                onChange={(val) => {
                                                    setSelectedInstanceId(val);
                                                    setSelectedContainerId('');
                                                    setDiscoveredDatabases([]);
                                                    if (credentialScopeLevel === 'INSTANCE' && selectedCredentialId) {
                                                        discoverInstanceTargets(val, selectedInterfaceId, selectedCredentialId);
                                                    }
                                                }}
                                                placeholder={instanceOptions.length > 0 ? "Choose Service Instance ..." : "No instances found (+ Add New)"}
                                                disabled={instanceOptions.length === 0}
                                                className="w-full font-mono"
                                            />
                                        )}
                                    </div>

                                    {/* CONTEXTUAL INJECTION POINT A: SERVICE INSTANCE LEVEL */}
                                    {selectedInstanceId && credentialScopeLevel !== 'CONTAINER' && (
                                        <div className="animate-fade-in">
                                            {credentialScopeLevel === 'INSTANCE' ? (
                                                renderCredentialSubSection('INSTANCE')
                                            ) : (
                                                <div className="py-0.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => setCredentialScopeLevel('INSTANCE')}
                                                        className="w-full py-1.5 px-3 border border-dashed border-amber-500/60 hover:border-amber-400 bg-amber-950/20 hover:bg-amber-950/40 text-amber-300 rounded text-[11px] font-sans flex items-center justify-center gap-1.5 transition-all shadow-sm"
                                                        title="Insert Credentials at Service Instance Level (Inherited by all databases)"
                                                    >
                                                        <span className="material-symbols-outlined text-[14px]">key</span>
                                                        &larr; Insert credentials here (Service Instance Level)
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                </div>
                            ) : null}

                            {/* 2.3 Target Container (Nested Level 3 - Indented Further) */}
                            {selectedHostId && selectedInterfaceId && selectedInstanceId ? (
                                <div className="pl-8 border-l-2 border-teal-500/30 space-y-2 pt-1 animate-fade-in">
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-slate-300 font-medium text-[11px] flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[13px] text-teal-400">table_rows</span>
                                            Target Container (Database / Tenant)
                                        </label>
                                        <div className="flex items-center gap-1">
                                            {selectedContainerId && selectedContainerId !== '__INSTANCE__' && !selectedContainerId.startsWith('__LIVE__:') && !isAddingContainer && !isEditingContainer && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const c = topology.containers.find(x => String(x.ContainerID) === String(selectedContainerId));
                                                        if (c) {
                                                            setNewContainerForm({
                                                                ContainerName: c.ContainerName || '',
                                                                ContainerType: c.ContainerType || 'RELATIONAL_DATABASE',
                                                                ContainerRole: c.ContainerRole || 'PRODUCTION',
                                                                OrganizationID: c.OrganizationID ? String(c.OrganizationID) : ''
                                                            });
                                                            setIsAddingContainer(false);
                                                            setIsEditingContainer(true);
                                                        }
                                                    }}
                                                    className="p-1 rounded text-sky-400 hover:text-sky-300 hover:bg-slate-800 transition-colors"
                                                    title="Edit Selected Container"
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">edit</span>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (isAddingContainer || isEditingContainer) {
                                                        setIsAddingContainer(false);
                                                        setIsEditingContainer(false);
                                                    } else {
                                                        setIsAddingContainer(true);
                                                        setIsEditingContainer(false);
                                                        setNewContainerForm({ ContainerName: '', ContainerType: 'RELATIONAL_DATABASE', ContainerRole: 'PRODUCTION', OrganizationID: '' });
                                                    }
                                                }}
                                                className="p-1 rounded text-emerald-400 hover:text-emerald-300 hover:bg-slate-800 transition-colors"
                                                title={(isAddingContainer || isEditingContainer) ? 'Cancel' : 'Add Container'}
                                            >
                                                <span className="material-symbols-outlined text-[14px]">{(isAddingContainer || isEditingContainer) ? 'close' : 'add'}</span>
                                            </button>
                                        </div>
                                    </div>

                                    {(isAddingContainer || isEditingContainer) ? (
                                        <div className="p-2.5 bg-slate-900/60 rounded border border-slate-800 space-y-2 animate-fade-in">
                                            <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                                                <span className="text-[10px] font-bold text-teal-300">
                                                    {isEditingContainer ? 'Edit Target Container' : 'Add Target Container'}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => { setIsAddingContainer(false); setIsEditingContainer(false); }}
                                                    className="p-0.5 text-slate-400 hover:text-white"
                                                    title="Cancel"
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">close</span>
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2">
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Container / DB Name</label>
                                                    <input
                                                        type="text"
                                                        required
                                                        value={newContainerForm.ContainerName}
                                                        onChange={e => setNewContainerForm({ ...newContainerForm, ContainerName: e.target.value })}
                                                        placeholder="e.g. pbsdata00"
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs font-mono"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Container Type</label>
                                                    <select
                                                        value={newContainerForm.ContainerType}
                                                        onChange={e => setNewContainerForm({ ...newContainerForm, ContainerType: e.target.value })}
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                    >
                                                        <option value="RELATIONAL_DATABASE">Relational Database</option>
                                                        <option value="SAAS_TENANT">SaaS Tenant</option>
                                                        <option value="DOCUMENT_STORE">Document Store</option>
                                                        <option value="FILE_SYSTEM">File System</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-slate-400 block mb-0.5">Role</label>
                                                    <select
                                                        value={newContainerForm.ContainerRole}
                                                        onChange={e => setNewContainerForm({ ...newContainerForm, ContainerRole: e.target.value })}
                                                        className="w-full bg-slate-900 border border-slate-700/80 rounded px-2 py-1 text-white text-xs"
                                                    >
                                                        <option value="PRODUCTION">Production</option>
                                                        <option value="SANDBOX">Sandbox</option>
                                                        <option value="STAGING">Staging</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="flex justify-end items-center gap-2 pt-1">
                                                <button
                                                    type="button"
                                                    onClick={() => { setIsAddingContainer(false); setIsEditingContainer(false); }}
                                                    className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 text-xs"
                                                    title="Cancel"
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">close</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={isEditingContainer ? handleUpdateContainer : handleCreateContainer}
                                                    className="px-2.5 py-1 bg-teal-600 hover:bg-teal-500 text-white rounded text-[11px] font-semibold flex items-center gap-1 shadow transition-colors"
                                                    title="Save Container"
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">save</span>
                                                    <span>{isEditingContainer ? 'Update Container' : 'Save Container'}</span>
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <SearchableSelect
                                            options={containerOptions}
                                            value={selectedContainerId}
                                            onChange={(val) => {
                                                setSelectedContainerId(val);
                                            }}
                                            placeholder="Choose Target Container / Scope ..."
                                            className="w-full font-mono"
                                        />
                                    )}

                                    {/* CONTEXTUAL INJECTION POINT B: TARGET CONTAINER LEVEL */}
                                    {selectedContainerId && credentialScopeLevel !== 'INSTANCE' && (
                                        <div className="animate-fade-in">
                                            {credentialScopeLevel === 'CONTAINER' ? (
                                                renderCredentialSubSection('CONTAINER')
                                            ) : (
                                                <div className="py-0.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => setCredentialScopeLevel('CONTAINER')}
                                                        className="w-full py-1.5 px-3 border border-dashed border-teal-500/60 hover:border-teal-400 bg-teal-950/20 hover:bg-teal-950/40 text-teal-300 rounded text-[11px] font-sans flex items-center justify-center gap-1.5 transition-all shadow-sm"
                                                        title="Insert Credentials at Target Container Level (Database-specific)"
                                                    >
                                                        <span className="material-symbols-outlined text-[14px]">key</span>
                                                        &larr; Insert credentials here (Target Container Level)
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                </div>
                            ) : null}

                        </div>

                        {/* Test Result Display */}
                        {testResult && (
                            <div className={`p-2 rounded border text-xs animate-fade-in ${
                                testResult.success ? 'bg-emerald-950/80 border-emerald-800 text-emerald-300' : 'bg-red-950/80 border-red-800 text-red-300'
                            }`}>
                                <div className="flex items-center gap-1.5 font-bold text-xs mb-0.5">
                                    <span className="material-symbols-outlined text-[14px]">
                                        {testResult.success ? 'check_circle' : 'error'}
                                    </span>
                                    <span>{testResult.success ? 'Connection Successful!' : 'Connection Failed'}</span>
                                    {testResult.latencyMs && (
                                        <span className="text-[10px] font-mono text-slate-400">({testResult.latencyMs}ms)</span>
                                    )}
                                </div>
                                <div className="text-[11px] font-mono whitespace-pre-wrap">{testResult.message || testResult.error}</div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="pt-2.5 mt-auto flex items-center justify-between gap-2 border-t border-slate-800 sticky bottom-0 bg-slate-950/95 py-2">
                            <button
                                type="button"
                                disabled={testingConnection || (!isSaaSTarget && (!selectedHostId || !selectedInstanceId))}
                                onClick={handleTest}
                                className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-40"
                                title="Test connection reachability"
                            >
                                <span className={`material-symbols-outlined text-[14px] ${testingConnection ? 'animate-spin' : ''}`}>
                                    {testingConnection ? 'progress_activity' : 'network_check'}
                                </span>
                                {testingConnection ? 'Testing...' : 'Test Connection'}
                            </button>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-3 py-1 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded text-xs font-semibold transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || (!isSaaSTarget && (!selectedHostId || !selectedInstanceId))}
                                    className="px-3.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all disabled:opacity-40"
                                >
                                    <span className="material-symbols-outlined text-[15px]">
                                        {saving ? 'progress_activity' : 'save'}
                                    </span>
                                    {saving ? 'Saving...' : (editingConnection ? 'Save Changes' : 'Create & Bind')}
                                </button>
                            </div>
                        </div>

                    </form>
                )}
            </div>
        </div>
    );
}
